import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Terminal, Zap, Boxes } from "lucide-react";

const STACK = ["Golang", "PHP", "C", "C++", "Python", "Pandas", "Panda3D", "Matlab", "MySQL", "PostgreSQL", "Redis", "Memcached", "Docker", "Kubernetes", "gRPC", "WebSocket"];

export const Hero = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const cta = () => (user ? navigate("/workspace") : login());

  return (
    <section className="relative overflow-hidden grid-pattern pt-20 pb-24" id="top">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[340px] bg-emerald-500/15 blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" /> Intelligent Digital Twin Engine
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
            Build game backends{" "}
            <span className="text-emerald-400 text-glow">100× faster</span>{" "}
            with a digital twin agent
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
            Describe your game's business requirements. Wiwi analyzes them and auto-generates production backend architecture, Golang & PHP code, DB schemas, caching layers, Dockerfiles and Kubernetes manifests — tuned for turn-based and real-time systems.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button onClick={cta} data-testid="hero-launch-workspace-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-12 px-7 text-base group">
              Launch Workspace <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button onClick={() => document.getElementById("digital-twin")?.scrollIntoView({ behavior: "smooth" })}
              variant="outline" data-testid="hero-demo-btn"
              className="rounded-full border-slate-700 bg-slate-900/50 text-slate-200 hover:bg-slate-800 hover:text-white h-12 px-7 text-base">
              <Terminal className="w-4 h-4 mr-1" /> See it generate
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
            {[["100×", "faster delivery"], ["<5ms", "cache latency"], ["99.9%", "uptime target"]].map(([a, b]) => (
              <div key={a}>
                <div className="font-display text-2xl font-bold text-white">{a}</div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{b}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="glass rounded-2xl border border-slate-800 shadow-2xl shadow-black/50 overflow-hidden glow-emerald">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-950/60">
              <span className="w-3 h-3 rounded-full bg-red-500/70" />
              <span className="w-3 h-3 rounded-full bg-amber-500/70" />
              <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
              <span className="ml-2 text-xs font-mono text-slate-500">wiwi-agent · matchmaking.go</span>
            </div>
            <pre className="p-5 text-[12.5px] leading-relaxed font-mono text-slate-300 overflow-x-auto">
<span className="text-slate-600">// Turn-based Elo matchmaking service</span>{"\n"}
<span className="text-cyan-400">package</span> matchmaking{"\n\n"}
<span className="text-cyan-400">func</span> <span className="text-emerald-400">Enqueue</span>(ctx <span className="text-amber-300">context.Context</span>, p <span className="text-amber-300">*Player</span>) <span className="text-amber-300">error</span> {"{"}{"\n"}
{"  "}key := fmt.Sprintf(<span className="text-emerald-300">"queue:elo:%d"</span>, bucket(p.Elo)){"\n"}
{"  "}<span className="text-cyan-400">return</span> rdb.ZAdd(ctx, key, redis.Z{"{"}{"\n"}
{"    "}Score: <span className="text-amber-300">float64</span>(p.Elo),{"\n"}
{"    "}Member: p.ID,{"\n"}
{"  "}{"}"}).Err(){"\n"}
{"}"}
            </pre>
            <div className="px-4 py-3 border-t border-slate-800 bg-slate-950/60 flex items-center gap-2 text-xs font-mono text-emerald-400">
              <Zap className="w-3.5 h-3.5" /> generated by digital twin · Redis + Golang
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-16 overflow-hidden border-y border-slate-800/60 py-5 bg-slate-950/40">
        <div className="flex gap-10 animate-marquee whitespace-nowrap w-max">
          {[...STACK, ...STACK].map((s, i) => (
            <span key={`${s}-${i}`} className="inline-flex items-center gap-2 text-sm font-mono text-slate-500">
              <Boxes className="w-4 h-4 text-emerald-500/60" /> {s}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
